import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, ChevronDown, User } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { Logo } from "./Logo";
import { fetchRoster, fetchCollections, fetchNavItems } from "../lib/api";

export const Header = () => {
  const { t, lang, setLang, currency, setCurrency, currencies, itemCount, setCartOpen } = useApp();
  const { user } = useAuth();
  const { s, site } = useSite();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [roster, setRoster] = useState({ wrestlers: [], musicians: [] });
  const [collections, setCollections] = useState([]);
  const [navItems, setNavItems] = useState([]);

  useEffect(() => {
    fetchRoster().then(setRoster).catch(() => {});
    fetchCollections().then((d) => setCollections(d.collections || [])).catch(() => {});
    fetchNavItems().then((d) => setNavItems(d.items || [])).catch(() => {});
  }, []);

  // Resolve a nav URL: if admin set `nav_{key}_url`, use that; otherwise fall
  // back to the default route. External URLs (http/https) render as <a>; internal
  // routes render as <Link>.
  const navUrl = (key, fallback) => (site[`nav_${key}_url`] || fallback || "").trim();
  const isExternal = (u) => u.startsWith("http://") || u.startsWith("https://");

  // Allow each nav item to be hidden by setting nav_{key}_hidden=true
  const hidden = (key) => site[`nav_${key}_hidden`] === true || String(site[`nav_${key}_hidden`] || "").toLowerCase() === "true";

  const NavItem = ({ navKey, defaultTo, label, testId, accent }) => {
    if (hidden(navKey)) return null;
    const to = navUrl(navKey, defaultTo);
    const cls = `font-mono text-xs tracking-[0.25em] kult-link ${accent ? "text-kult-yellow inline-flex items-center gap-1.5" : "text-white"}`;
    if (isExternal(to)) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>
          {accent ? "⚡ " : ""}{label}
        </a>
      );
    }
    return (
      <NavLink
        to={to}
        className={({ isActive }) => `${cls} ${isActive && !accent ? "text-kult-pink" : ""}`}
        data-testid={testId}
      >
        {accent ? "⚡ " : ""}{label}
      </NavLink>
    );
  };

  return (
    <header
      className="sticky top-0 z-50 border-b border-kult-border bg-kult-black/85 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Logo size={28} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {/* SHOP DROPDOWN */}
          <div className="group relative" data-testid="nav-shop-dropdown">
            <Link
              to="/shop"
              className="font-mono text-xs tracking-[0.25em] kult-link inline-flex items-center gap-1"
              data-testid="nav-shop"
            >
              {s("nav_shop", t.nav.shop)}
              <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
            </Link>
            <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all absolute top-full left-0 pt-3 w-[640px]">
              <div className={`bg-kult-black border-2 border-kult-pink p-6 grid gap-6 kult-shadow-yellow ${
                ([s("shop_menu_col1_hidden", "") !== "true", s("shop_menu_col2_hidden", "") !== "true", s("shop_menu_col3_hidden", "") !== "true"].filter(Boolean).length === 1)
                  ? "grid-cols-1 w-[360px]"
                  : ([s("shop_menu_col1_hidden", "") !== "true", s("shop_menu_col2_hidden", "") !== "true", s("shop_menu_col3_hidden", "") !== "true"].filter(Boolean).length === 2)
                  ? "grid-cols-2 w-[500px]"
                  : "grid-cols-3"
              }`}>
                {s("shop_menu_col1_hidden", "") !== "true" && (
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mb-3">
                      {s("shop_menu_col1_title", "COLLECTIONS")}
                    </p>
                    <ul className="space-y-2 font-display text-lg">
                      <li><Link to="/shop" className="kult-link" data-testid="dd-shop-all">{lang === "pt" ? "TODA A LOJA" : "ALL"}</Link></li>
                      {collections
                        .filter((c) => c.slug !== "all" && c.show_in_shop_menu !== false)
                        .map((c) => (
                          <li key={c.slug}>
                            <Link
                              to={`/shop?collection=${c.slug}`}
                              className="kult-link"
                              data-testid={`dd-shop-${c.slug}`}
                            >
                              {lang === "pt" ? c.name_pt : c.name_en}
                            </Link>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
                {s("shop_menu_col2_hidden", "") !== "true" && (
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mb-3">
                      {s("shop_menu_col2_title", t.nav.shop_by_wrestler)}
                    </p>
                    <ul className="space-y-2 font-display text-lg">
                      {roster.wrestlers.length === 0 && (
                        <li className="font-mono text-xs text-kult-muted">—</li>
                      )}
                      {roster.wrestlers.map((w) => (
                        <li key={w.slug}>
                          <Link
                            to={`/artists/${w.slug}`}
                            className="kult-link"
                            data-testid={`dd-wrestler-${w.slug}`}
                          >
                            {w.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {s("shop_menu_col3_hidden", "") !== "true" && (
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mb-3">
                      {s("shop_menu_col3_title", t.nav.shop_by_artist)}
                    </p>
                    <ul className="space-y-2 font-display text-lg">
                      {roster.musicians.length === 0 && (
                        <li className="font-mono text-xs text-kult-muted">—</li>
                      )}
                      {roster.musicians.map((m) => (
                        <li key={m.slug}>
                          <Link
                            to={`/artists/${m.slug}`}
                            className="kult-link"
                            data-testid={`dd-artist-${m.slug}`}
                          >
                            {m.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          <NavItem navKey="tickets" defaultTo="/tickets" label={s("nav_tickets", t.nav.tickets)} testId="nav-tickets" accent />
          <NavItem navKey="events" defaultTo="/events" label={s("nav_events", t.nav.events)} testId="nav-events" />
          <NavItem navKey="roster" defaultTo="/artists" label={s("nav_roster", t.nav.artists)} testId="nav-roster" />
          <NavItem navKey="about" defaultTo="/about" label={s("nav_about", t.nav.about)} testId="nav-about" />

          {/* HELP dropdown — FAQ / Delivery / Contact */}
          {s("nav_help_hidden", "") !== "true" && (
            <div className="relative group">
              <button className="font-mono text-xs tracking-[0.25em] text-white kult-link inline-flex items-center gap-1" data-testid="nav-help">
                {s("nav_help", lang === "pt" ? "AJUDA" : "HELP")}
                <ChevronDown size={12} />
              </button>
              <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all absolute top-full left-0 pt-3 w-[280px]">
                <div className="bg-kult-black border-2 border-kult-pink p-5 kult-shadow-yellow">
                  <ul className="space-y-2 font-display text-lg">
                    <li><Link to="/help/faq" className="kult-link" data-testid="dd-help-faq">{s("nav_help_faq", "FAQ")}</Link></li>
                    <li><Link to="/help/delivery" className="kult-link" data-testid="dd-help-delivery">{s("nav_help_delivery", lang === "pt" ? "INFO DE ENVIO" : "DELIVERY INFO")}</Link></li>
                    <li><Link to="/help/contact" className="kult-link" data-testid="dd-help-contact">{s("nav_help_contact", lang === "pt" ? "CONTACTO" : "CONTACT")}</Link></li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Custom nav items added via Admin → NAV tab */}
          {navItems.map((item) => {
            const label = (lang === "pt" ? item.label_pt : item.label_en) || item.label_en;
            const url = item.url || "/";
            const isExt = item.external || url.startsWith("http://") || url.startsWith("https://");
            const cls = `font-mono text-xs tracking-[0.25em] kult-link ${item.accent ? "text-kult-yellow inline-flex items-center gap-1.5" : "text-white"}`;
            if (isExt) {
              return (
                <a
                  key={item.id}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cls}
                  data-testid={`nav-custom-${item.id}`}
                >
                  {item.accent ? "⚡ " : ""}{label}
                </a>
              );
            }
            return (
              <NavLink
                key={item.id}
                to={url}
                className={({ isActive }) => `${cls} ${isActive && !item.accent ? "text-kult-pink" : ""}`}
                data-testid={`nav-custom-${item.id}`}
              >
                {item.accent ? "⚡ " : ""}{label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <Link
            to={
              user && user.role
                ? user.role === "admin"
                  ? "/portal/admin"
                  : user.role === "artist"
                  ? "/portal/artist"
                  : "/portal/customer"
                : "/portal/login"
            }
            className="hidden md:inline-flex items-center gap-1.5 border border-kult-border px-3 py-2 hover:border-kult-pink transition-colors"
            data-testid="header-portal-link"
            title="Portal"
          >
            <User size={14} />
            <span className="font-mono text-[10px] tracking-[0.25em]">
              {user && user.role ? "PORTAL" : "PORTAL"}
            </span>
          </Link>
          <div className="font-mono text-xs flex items-center border border-kult-border" data-testid="lang-toggle">
            <button
              onClick={() => setLang("en")}
              className={`px-2 py-1 ${lang === "en" ? "bg-kult-yellow text-black" : "text-white"}`}
              data-testid="lang-en"
            >EN</button>
            <button
              onClick={() => setLang("pt")}
              className={`px-2 py-1 border-l border-kult-border ${lang === "pt" ? "bg-kult-yellow text-black" : "text-white"}`}
              data-testid="lang-pt"
            >PT</button>
          </div>

          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="font-mono text-xs bg-transparent border border-kult-border px-2 py-1 text-white hover:border-kult-pink focus:border-kult-pink outline-none cursor-pointer hidden sm:inline-block"
            data-testid="currency-select"
            aria-label="Currency"
          >
            {currencies.map((c) => (
              <option key={c.code} value={c.code} className="bg-kult-black">{c.code}</option>
            ))}
          </select>

          <button
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 px-3 py-2 border border-kult-border hover:border-kult-pink transition-colors"
            data-testid="cart-button"
          >
            <ShoppingBag size={16} />
            <span className="font-mono text-xs tracking-widest hidden md:inline">{t.nav.cart}</span>
            {itemCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-kult-pink text-black font-mono text-[10px] px-1.5 py-0.5 leading-none" data-testid="cart-count">
                {itemCount}
              </span>
            )}
          </button>

          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2"
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {mobileOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[60] bg-black flex flex-col p-6 overflow-y-auto" data-testid="mobile-menu">
          <div className="flex items-center justify-between mb-10">
            <Logo size={26} />
            <button onClick={() => setMobileOpen(false)} aria-label="Close" data-testid="mobile-menu-close">
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col gap-5">
            {!hidden("shop") && (
              <Link to={navUrl("shop", "/shop")} onClick={() => setMobileOpen(false)} className="font-display text-4xl">{t.nav.shop}</Link>
            )}
            {!hidden("tickets") && (
              <Link to={navUrl("tickets", "/tickets")} onClick={() => setMobileOpen(false)} className="font-display text-4xl text-kult-yellow">⚡ {t.nav.tickets}</Link>
            )}
            {!hidden("events") && (
              <Link to={navUrl("events", "/events")} onClick={() => setMobileOpen(false)} className="font-display text-4xl">{t.nav.events}</Link>
            )}
            {!hidden("roster") && (
              <Link to={navUrl("roster", "/artists")} onClick={() => setMobileOpen(false)} className="font-display text-4xl">{t.nav.artists}</Link>
            )}
            {!hidden("about") && (
              <Link to={navUrl("about", "/about")} onClick={() => setMobileOpen(false)} className="font-display text-4xl">{t.nav.about}</Link>
            )}
            {s("nav_help_hidden", "") !== "true" && (
              <>
                <Link to="/help/faq" onClick={() => setMobileOpen(false)} className="font-display text-4xl">{s("nav_help_faq", "FAQ")}</Link>
                <Link to="/help/delivery" onClick={() => setMobileOpen(false)} className="font-display text-4xl">{s("nav_help_delivery", lang === "pt" ? "INFO DE ENVIO" : "DELIVERY INFO")}</Link>
                <Link to="/help/contact" onClick={() => setMobileOpen(false)} className="font-display text-4xl">{s("nav_help_contact", lang === "pt" ? "CONTACTO" : "CONTACT")}</Link>
              </>
            )}
            {navItems.map((item) => {
              const lbl = (lang === "pt" ? item.label_pt : item.label_en) || item.label_en;
              const url = item.url || "/";
              const isExt = item.external || url.startsWith("http://") || url.startsWith("https://");
              const cls = `font-display text-4xl ${item.accent ? "text-kult-yellow" : ""}`;
              return isExt ? (
                <a key={item.id} href={url} target="_blank" rel="noopener noreferrer" className={cls} onClick={() => setMobileOpen(false)}>
                  {item.accent ? "⚡ " : ""}{lbl}
                </a>
              ) : (
                <Link key={item.id} to={url} onClick={() => setMobileOpen(false)} className={cls}>
                  {item.accent ? "⚡ " : ""}{lbl}
                </Link>
              );
            })}

            <div className="border-t border-kult-border mt-6 pt-6">
              <Link
                to={
                  user && user.role
                    ? user.role === "admin"
                      ? "/portal/admin"
                      : user.role === "artist"
                      ? "/portal/artist"
                      : "/portal/customer"
                    : "/portal/login"
                }
                onClick={() => setMobileOpen(false)}
                className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] border border-kult-border px-3 py-2 hover:border-kult-pink"
                data-testid="mobile-portal-link"
              >
                <User size={14} /> PORTAL
              </Link>
              <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mt-8 mb-3">COLLECTIONS</p>
              {collections
                .filter((c) => c.show_in_shop_menu !== false)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={c.slug === "all" ? "/shop" : `/shop?collection=${c.slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="block font-display text-2xl mb-2"
                  >
                    {lang === "pt" ? c.name_pt : c.name_en}
                  </Link>
                ))}
              {roster.wrestlers.length > 0 && (
                <>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mt-6 mb-3">BY WRESTLER</p>
                  {roster.wrestlers.map((w) => (
                    <Link key={w.slug} to={`/artists/${w.slug}`} onClick={() => setMobileOpen(false)} className="block font-display text-2xl mb-2">
                      {w.name}
                    </Link>
                  ))}
                </>
              )}
              {roster.musicians.length > 0 && (
                <>
                  <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mt-6 mb-3">BY ARTIST</p>
                  {roster.musicians.map((m) => (
                    <Link key={m.slug} to={`/artists/${m.slug}`} onClick={() => setMobileOpen(false)} className="block font-display text-2xl mb-2">
                      {m.name}
                    </Link>
                  ))}
                </>
              )}
            </div>
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
};
